/**
 * Component Health Status
 *
 * Shows real-time health status of all local AI components
 * with controls to start/stop individual components.
 */

import React, { useCallback, useState } from 'react';
import {
  RefreshCw,
  Play,
  Square,
  AlertCircle,
  CheckCircle,
  Clock,
  Activity,
  Download,
} from 'lucide-react';

import { useTranslation } from '../../hooks/useTranslation';
import { useComponentHealth } from '../../hooks/useComponentHealth';
import { getHealthStatusBadgeColors, formatUptime } from '../../services/localFirstService';
import { installComponent } from '../../services/componentService';
import type { ComponentHealth, HealthStatus } from '../../services/localFirstService';

// ============================================================================
// Types
// ============================================================================

interface HealthCardProps {
  health: ComponentHealth;
  onStart: () => Promise<void>;
  onStop: () => Promise<void>;
  onInstall: () => Promise<void>;
}

// ============================================================================
// StatusBadge Sub-component
// ============================================================================

const StatusBadge: React.FC<{ status: HealthStatus }> = ({ status }) => {
  const { t } = useTranslation();

  const statusLabels: Record<HealthStatus, string> = {
    healthy: t('health.healthy', { defaultValue: 'Healthy' }),
    degraded: t('health.degraded', { defaultValue: 'Degraded' }),
    unhealthy: t('health.unhealthy', { defaultValue: 'Unhealthy' }),
    unknown: t('health.unknown', { defaultValue: 'Unknown' }),
  };

  const StatusIcon = () => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-3 w-3" />;
      case 'degraded':
        return <Clock className="h-3 w-3" />;
      case 'unhealthy':
        return <AlertCircle className="h-3 w-3" />;
      default:
        return <Activity className="h-3 w-3" />;
    }
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getHealthStatusBadgeColors(status)}`}
    >
      <StatusIcon />
      {statusLabels[status]}
    </span>
  );
};

// ============================================================================
// HealthCard Sub-component
// ============================================================================

const HealthCard: React.FC<HealthCardProps> = ({ health, onStart, onStop, onInstall }) => {
  const { t } = useTranslation();
  const [actionLoading, setActionLoading] = useState(false);

  const handleStart = useCallback(async () => {
    setActionLoading(true);
    try {
      await onStart();
    } finally {
      setActionLoading(false);
    }
  }, [onStart]);

  const handleStop = useCallback(async () => {
    setActionLoading(true);
    try {
      await onStop();
    } finally {
      setActionLoading(false);
    }
  }, [onStop]);

  const handleInstall = useCallback(async () => {
    setActionLoading(true);
    try {
      await onInstall();
    } finally {
      setActionLoading(false);
    }
  }, [onInstall]);

  // Format component ID for display (snake_case to Title Case)
  const displayName = health.componentId
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return (
    <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-secondary-900 dark:text-secondary-100">{displayName}</h4>
        <StatusBadge status={health.status} />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-2 text-sm mb-4">
        <div className="text-secondary-600 dark:text-secondary-400">
          {t('health.installed', { defaultValue: 'Installed' })}:
        </div>
        <div className="text-secondary-900 dark:text-secondary-100">
          {health.isInstalled
            ? t('common.yes', { defaultValue: 'Yes' })
            : t('common.no', { defaultValue: 'No' })}
        </div>

        <div className="text-secondary-600 dark:text-secondary-400">
          {t('health.running', { defaultValue: 'Running' })}:
        </div>
        <div className="text-secondary-900 dark:text-secondary-100">
          {health.isRunning
            ? t('common.yes', { defaultValue: 'Yes' })
            : t('common.no', { defaultValue: 'No' })}
        </div>

        {health.uptimeSeconds > 0 && (
          <>
            <div className="text-secondary-600 dark:text-secondary-400">
              {t('health.uptime', { defaultValue: 'Uptime' })}:
            </div>
            <div className="text-secondary-900 dark:text-secondary-100">
              {formatUptime(health.uptimeSeconds)}
            </div>
          </>
        )}

        {health.responseTimeMs !== null && (
          <>
            <div className="text-secondary-600 dark:text-secondary-400">
              {t('health.responseTime', { defaultValue: 'Response' })}:
            </div>
            <div className="text-secondary-900 dark:text-secondary-100">
              {Math.round(health.responseTimeMs)}ms
            </div>
          </>
        )}

        {health.restartCount > 0 && (
          <>
            <div className="text-secondary-600 dark:text-secondary-400">
              {t('health.restarts', { defaultValue: 'Restarts' })}:
            </div>
            <div className="text-secondary-900 dark:text-secondary-100">{health.restartCount}</div>
          </>
        )}
      </div>

      {/* Error Message */}
      {health.lastError && (
        <div className="mb-4 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-700 dark:text-red-300 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span className="break-words">{health.lastError}</span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        {!health.isInstalled ? (
          <button
            onClick={handleInstall}
            disabled={actionLoading}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {t('health.install', { defaultValue: 'Install' })}
          </button>
        ) : !health.isRunning ? (
          <button
            onClick={handleStart}
            disabled={actionLoading}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {t('health.start', { defaultValue: 'Start' })}
          </button>
        ) : (
          <button
            onClick={handleStop}
            disabled={actionLoading}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Square className="h-4 w-4" />
            )}
            {t('health.stop', { defaultValue: 'Stop' })}
          </button>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const ComponentHealthStatus: React.FC = () => {
  const { t } = useTranslation();
  const { health, loading, error, refresh, start, stop } = useComponentHealth();
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  }, [refresh]);

  // Loading state
  if (loading) {
    return (
      <div className="animate-pulse grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-40 bg-secondary-200 dark:bg-secondary-700 rounded-lg" />
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-red-600 dark:text-red-400 p-4 rounded-lg bg-red-50 dark:bg-red-900/20">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="h-5 w-5" />
          <span className="font-medium">{error}</span>
        </div>
        <button
          onClick={handleRefresh}
          className="text-sm text-red-700 dark:text-red-300 hover:underline flex items-center gap-1"
        >
          <RefreshCw className="h-3 w-3" />
          {t('common.retry', { defaultValue: 'Retry' })}
        </button>
      </div>
    );
  }

  const healthEntries = Object.values(health);

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 flex items-center gap-2">
            <Activity className="h-5 w-5" />
            {t('health.title', { defaultValue: 'Runtime Services' })}
          </h3>
          <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
            {t('health.subtitle', {
              defaultValue:
                'Monitors local services with a managed process (e.g. Ollama, Fooocus). Installed models without a runtime process are managed in the Installations tab.',
            })}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="px-3 py-1.5 text-sm text-secondary-700 dark:text-secondary-300 border border-secondary-300 dark:border-secondary-600 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors disabled:opacity-50 flex items-center gap-2 flex-shrink-0"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {t('health.refresh', { defaultValue: 'Refresh' })}
        </button>
      </div>

      {/* Empty State */}
      {healthEntries.length === 0 ? (
        <div className="text-center py-8 text-secondary-500 dark:text-secondary-400">
          <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>{t('health.noComponents', { defaultValue: 'No runtime services active' })}</p>
          <p className="text-sm mt-1">
            {t('health.installHint', {
              defaultValue:
                'Services like Ollama and Fooocus appear here when running. Install them from the Installations tab.',
            })}
          </p>
        </div>
      ) : (
        /* Component Grid */
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {healthEntries.map((h) => (
            <HealthCard
              key={h.componentId}
              health={h}
              onStart={async () => {
                await start(h.componentId);
              }}
              onStop={async () => {
                await stop(h.componentId);
              }}
              onInstall={async () => {
                await installComponent(h.componentId);
                // Refresh health after install starts
                await refresh();
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ComponentHealthStatus;
